"use client";

import { caseMaterialLabel } from "@/utils/case-material";

import type { CaseSectionProps } from "../model";
import { getClassicPropertyFacts } from "@/utils/classic-property-facts";
import SectionLayout from "@/components/ui/SectionLayout";
import { getDefaultCmsSection } from "@/utils/helper";
import ContentDialog from "@/components/ui/ContentDialog";
import { isLookSystem } from "@/utils/look-gate";
import ViewingForm from "@/components/forms/ViewingForm";
import BidForm from "@/components/forms/BidForm";
import DownloadDocumentForm from "@/components/forms/DownloadDocumentForm";
import OpenHouseForm from "@/components/forms/OpenhouseForm";
import { EnergyLabelRange } from "@/components/ui/EnergyLabel";

export default function ClassicCasePresentation({
  caseData,
  cmsData,
  cmsSection,
  app,
  pageData,
}: CaseSectionProps<{
  presentation?: "classic";
  showAgent?: boolean;
  downloadFormTextColor?: string;
}>) {
  const description =
    caseData.marketingTexts?.description ||
    "Kontakt os for yderligere oplysninger om ejendommen.";
  const text = description
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const active = caseData.isActive && !caseData.isSold;
  const facts = getClassicPropertyFacts(caseData, true);
  const classic = isLookSystem(cmsData?.theme);
  return (
    <SectionLayout cmsSection={getDefaultCmsSection(cmsSection)}>
      <div className="re-classic-case-presentation">
        <div className="re-classic-case-description">
          <h1>
            {caseData.marketingTexts?.headline ||
              caseData.advertisementAddressOrDisplayName}
          </h1>
          {text.length > 1500 ? (
            <div className="re-classic-description-expand">
              <p className="re-classic-description-excerpt">
                {text.slice(0, 1500)}…
              </p>
              <details>
                <summary>
                  Læs mere <span aria-hidden>⌄</span>
                </summary>
                <div
                  className="styled-description"
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              </details>
            </div>
          ) : (
            <div
              className="styled-description"
              dangerouslySetInnerHTML={{ __html: description }}
            />
          )}
        </div>
        <div className="re-classic-case-facts">
          <h2>
            {caseData.shortAddress ||
              caseData.advertisementAddressOrDisplayName}
            <br />
            {caseData.zipAndCity}
          </h2>
          <dl>
            {facts.map((fact) => (
              <div key={fact.key}>
                <dt>{fact.label}:</dt>
                <dd
                  className={
                    fact.key === "energy" ? "re-classic-energy" : undefined
                  }
                >
                  {fact.key === "energy" ? (
                    <EnergyLabelRange
                      energyLabel={caseData.energyLabel}
                      energyLabelBest={caseData.energyLabelBest}
                      energyLabelWorst={caseData.energyLabelWorst}
                    />
                  ) : (
                    fact.value
                  )}
                </dd>
              </div>
            ))}
          </dl>
          {active && (
            <>
              <ContentDialog
                classic={classic}
                id={`case-${caseData.id}-viewing`}
                title="Ønske om fremvisning"
                triggerLabel="Bestil fremvisning"
              >
                <ViewingForm caseData={caseData} cmsData={cmsData} />
              </ContentDialog>
              {caseData.canSendBid && (
                <ContentDialog
                  classic={classic}
                  id={`case-${caseData.id}-bid`}
                  title="Giv et bud"
                  triggerLabel="Giv et bud"
                >
                  <BidForm caseData={caseData} cmsData={cmsData} />
                </ContentDialog>
              )}
              {caseData.documents?.length > 0 && (
                <ContentDialog
                  classic={classic}
                  id={`case-${caseData.id}-documents`}
                  title={caseMaterialLabel(caseData, "Bestil salgsopstilling")}
                >
                  <DownloadDocumentForm
                    caseData={caseData}
                    appData={app}
                    cmsData={cmsData}
                    pageData={pageData!}
                    cmsSection={cmsSection}
                  />
                </ContentDialog>
              )}
              {caseData.openhouses?.some((event) => event.bookingRequired) && (
                <ContentDialog
                  classic={classic}
                  id={`case-${caseData.id}-openhouse`}
                  title="Tilmeld åbent hus"
                  triggerLabel="Tilmeld åbent hus"
                >
                  <OpenHouseForm caseData={caseData} cmsData={cmsData} />
                </ContentDialog>
              )}
            </>
          )}
        </div>
      </div>
    </SectionLayout>
  );
}
